import { Button } from '@/global/components/Button'
import IconButton from '@/global/components/Button/IconButton'
import { cn } from '@/lib/utils'
import dayjs, { type Dayjs } from 'dayjs'
import 'dayjs/locale/pt-br'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { useState } from 'react'

type MonthCalendarProps = {
    /** Mês selecionado. Só ano e mês são considerados. */
    value?: Dayjs | null
    onChange: (competencia: Dayjs) => void
    /** Ano exibido ao abrir, quando não há valor. */
    defaultYear?: number
    className?: string
}

/**
 * `date(1)` **antes** de `month()`: rodando no dia 31, `month(1)` transbordaria
 * para março e o rótulo sairia trocado. Vale para os rótulos e para a data
 * emitida no clique.
 */
function primeiroDiaDoMes(ano: number, mes: number) {
    return dayjs().date(1).month(mes).year(ano).startOf('day')
}

const MESES = Array.from({ length: 12 }, (_, mes) =>
    primeiroDiaDoMes(dayjs().year(), mes).locale('pt-br').format('MMM'),
)

/**
 * Grid de 12 meses + navegação de ano — o `MonthCalendar` do MUI e as views
 * `['year','month']` do `DatePicker`. O react-day-picker não tem equivalente,
 * então é grid próprio; sem estado além do ano exibido.
 */
export function MonthCalendar({
    value,
    onChange,
    defaultYear,
    className,
}: MonthCalendarProps) {
    const [ano, setAno] = useState(
        () => value?.year() ?? defaultYear ?? dayjs().year(),
    )

    return (
        <div className={cn('w-[17.25rem] p-3', className)}>
            <div className="flex items-center justify-between">
                <IconButton
                    color="default"
                    title="Ano anterior"
                    aria-label="Ano anterior"
                    onClick={() => setAno(anterior => anterior - 1)}
                >
                    <ChevronLeftIcon />
                </IconButton>
                <span className="text-lg font-medium select-none">{ano}</span>
                <IconButton
                    color="default"
                    title="Próximo ano"
                    aria-label="Próximo ano"
                    onClick={() => setAno(anterior => anterior + 1)}
                >
                    <ChevronRightIcon />
                </IconButton>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2">
                {MESES.map((rotulo, mes) => {
                    const selecionado =
                        !!value && value.year() === ano && value.month() === mes
                    const mesStr = mes < 9 ? '0' + (mes + 1) : mes + 1
                    return (
                        <Button
                            key={rotulo}
                            type="button"
                            // O mês escolhido é o único preenchido; os demais
                            // são `text`, que é o `ghost` do `ui/button`.
                            variant={selecionado ? 'contained' : 'text'}
                            color={selecionado ? 'primary' : 'inherit'}
                            aria-pressed={selecionado}
                            className="min-w-0 px-2 py-1.5"
                            onClick={() => onChange(primeiroDiaDoMes(ano, mes))}
                        >
                            {mesStr} · {rotulo}
                        </Button>
                    )
                })}
            </div>
        </div>
    )
}
